// Relative, not the `@/` alias: this module is covered by `tests/session.test.ts`, which runs
// under `node --test` where only the bundler resolves the alias.
import { can, type SessionProfile } from '../auth/session.ts';

import { NAV_KEYS } from './navItems.ts';

/**
 * Which screen permission opens which navigation entry. Keys are `NavigationEntry.key`;
 * values are `platform.managed_tables.screen_key`. `null` means every signed-in user may open it.
 *
 * A key missing from this map is CLOSED, not open: a new page has to be placed here on purpose,
 * and `navIcons.test.ts` fails if a menu entry has no decision recorded. The fifteen keys that
 * left on 2026-09-25 - the fourteen prototypes plus `harvest`, which never had a screen - left
 * their decisions with them; the permissions they named still exist in the database, and
 * `PROTOTYPE_KEYS` in the manifest records which was which. `statement` came back the same day,
 * as a real screen, and brought its decision with it.
 */
export const NAV_SCREEN_KEYS: Record<string, string | null> = {
  dashboard: null,
  // Your own messages. Nothing to gate: the view shows the rows addressed to you and no others,
  // and comms.log - the administrator's log of everybody's - is a different screen entirely.
  'my-alerts': null,
  organisation: 'org.settings',
  // The same readers as before it moved off the Organisation page. Changing a factor is the app
  // owner's, asked by the server.
  'land-units': 'org.settings',
  'hsn-codes': 'org.settings',
  // Every signed-in user may see which organisation they belong to; the screen itself shows a
  // list only to an operator who actually holds more than one.
  organisations: null,
  users: 'user.list',
  onboarding: 'user.onboarding',
  access: 'role.configuration',
  teams: 'org.units',
  geography: 'geography.activation',
  doctypes: 'document.type.master',
  // Chambers and their norms are children of a work site and were written under the site's key
  // (platform.managed_tables says site.register for both), so the pages open with it too.
  chambers: 'site.register',
  'kyc-requirements': 'party.kyc.requirements',
  workflows: 'workflow.configuration',
  config: 'reference.data',
  // The log of everybody's messages. Templates and providers are tabs inside it, each shown only
  // to a role holding its own key (comms.template.master, comms.provider).
  comms: 'comms.log',
  'chamber-norms': 'site.register',
  'chamber-loads': 'site.register',
  'treatment-chemicals': 'site.register',
  'process-runs': 'site.register',
  kyc: 'party.kyc',
  // Same key as the templates it reviews.
  'consent-capture': 'party.consent',
  'plot-consents': 'party.consent',
  farmers: 'farmer.register',
  statement: 'farmer.statement',
  plots: 'plot.register',
  // The same plots, drawn. Boundaries also need plot.mapping read (the geometry policy).
  'plot-map': 'plot.register',
  inventory: 'plot.inventory',
  // The six practice registers, each taking the screen permission its records were ALREADY
  // written under inside the plot record - the same key `platform.managed_tables` holds for the
  // table. A page is not a new thing to be allowed; it is a second door onto rows a role could
  // always read, and giving it a key of its own would let the two doors disagree.
  silviculture: 'plot.inventory',
  inputs: 'plot.inventory',
  growth: 'plot.inventory',
  'sapling-issues': 'plot.inventory',
  'harvest-permits': 'plot.harvest',
  'farmer-sales': 'plot.harvest',
  'internal-permits': 'plot.harvest',
  'harvest-report': 'plot.harvest',
  'harvest-labour': 'plot.harvest',
  'yield-statement': 'plot.harvest',
  'sale-invoices': 'plot.harvest',
  'sale-dockets': 'plot.harvest',
  pests: 'plot.inventory',
  irrigation: 'plot.mapping',
  'soil-tests': 'plot.mapping',
  'risk-assessment': 'plot.register',
  inbox: 'approval.inbox',
  audit: 'audit.timeline',
  stakeholders: 'party.register',
  references: 'farmer.register',
  contractors: 'party.register',
  // The ecosystem reads parties, their roles and their accounts - all of which `party.register`
  // already governs. It gets no key of its own, because a directory that showed more than the
  // registers it summarises would be a way around them.
  ecosystem: 'party.register',
  engagements: 'party.register',
  workers: 'party.register',
  trades: 'plot.harvest',
  'annual-monitoring': 'plot.register',
  'internal-audits': 'plot.register',
  consultations: 'party.register',
  complaints: 'party.register',
  accidents: 'party.register',
  sites: 'site.register',
  satellite: 'plot.monitoring',
  'satellite-ops': 'plot.monitoring',
  'evidence-packs': 'evidence.pack',
  renewals: 'party.register',
};

export function canOpen(profile: SessionProfile | null | undefined, navKey: string): boolean {
  if (!(navKey in NAV_SCREEN_KEYS)) return false;
  const screenKey = NAV_SCREEN_KEYS[navKey];
  if (screenKey == null) return Boolean(profile);
  return can(profile, screenKey, 'read');
}

/** First page the user may open, for the post-login landing and for a refused route. */
export function landingKey(profile: SessionProfile | null | undefined, keys: string[] = NAV_KEYS): string {
  return keys.find((key) => canOpen(profile, key)) ?? 'dashboard';
}
